import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Calendar as CalendarIcon, Users, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';

const API = `${BACKEND_URL}/api`;

const CalendarioAsistencia = () => {
  const [empleados, setEmpleados] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [mesActual, setMesActual] = useState(new Date().getMonth() + 1);
  const [anoActual, setAnoActual] = useState(new Date().getFullYear());
  const [asistencias, setAsistencias] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    fetchEmpleados();
  }, []);

  useEffect(() => {
    if (empleadoSeleccionado) {
      fetchAsistencias();
    }
  }, [empleadoSeleccionado, mesActual, anoActual]);

  const fetchEmpleados = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      // Filtrar todos los roles que pueden crear reportes (excluir solo modelos y CEO)
      const empleadosFilter = response.data.filter(u => 
        u.role !== 'modelo' && u.role !== 'ceo'
      );
      setEmpleados(empleadosFilter);
      if (empleadosFilter.length > 0) {
        setEmpleadoSeleccionado(empleadosFilter[0]);
      }
    } catch (error) {
      console.error('Error fetching empleados:', error);
    }
  };

  const fetchAsistencias = async () => {
    if (!empleadoSeleccionado) return;
    
    setLoading(true);
    try {
      // Obtener asistencias del mes
      const asistenciasRes = await axios.get(
        `${API}/asistencia/empleado/${empleadoSeleccionado.id}`,
        {
          params: { mes: mesActual, ano: anoActual },
          withCredentials: true
        }
      );
      setAsistencias(asistenciasRes.data);

      // Obtener resumen
      const resumenRes = await axios.get(
        `${API}/asistencia/resumen/${empleadoSeleccionado.id}`,
        {
          params: { mes: mesActual, ano: anoActual },
          withCredentials: true
        }
      );
      setResumen(resumenRes.data);
    } catch (error) {
      console.error('Error fetching asistencias:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDiasDelMes = () => {
    const fecha = new Date(anoActual, mesActual - 1, 1);
    const ultimoDia = new Date(anoActual, mesActual, 0).getDate();
    const primerDiaSemana = fecha.getDay(); // 0 = Domingo, 1 = Lunes, etc.
    
    const dias = [];
    // Agregar días vacíos al inicio
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push(null);
    }
    // Agregar días del mes
    for (let dia = 1; dia <= ultimoDia; dia++) {
      dias.push(dia);
    }
    return dias;
  };

  const esDiaTrabajado = (dia) => {
    if (!dia) return false;
    const fechaBuscada = `${anoActual}-${String(mesActual).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    return asistencias.some(a => a.fecha === fechaBuscada);
  };

  const getHorasTrabajadas = (dia) => {
    if (!dia) return null;
    const fechaBuscada = `${anoActual}-${String(mesActual).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const asistencia = asistencias.find(a => a.fecha === fechaBuscada);
    return asistencia ? asistencia.horas_trabajadas : null;
  };

  const cambiarMes = (direccion) => {
    if (direccion === 'prev') {
      if (mesActual === 1) {
        setMesActual(12);
        setAnoActual(anoActual - 1);
      } else {
        setMesActual(mesActual - 1);
      }
    } else {
      if (mesActual === 12) {
        setMesActual(1);
        setAnoActual(anoActual + 1);
      } else {
        setMesActual(mesActual + 1);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header con selector de empleado */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-purple-400" />
            📅 Control de Asistencia
          </CardTitle>
          <p className="text-sm text-gray-400 mt-2">
            ℹ️ Un día cuenta como trabajado cuando se reportan al menos <span className="text-yellow-400 font-semibold">2 horas</span> de trabajo.
            <br />
            <span className="text-xs text-gray-500">Aplica para todos los empleados (Chatters, Asistentes, Administrativos, Uploaders, etc.)</span>
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex-1">
              <label className="text-gray-400 text-sm mb-2 block">Seleccionar Empleado</label>
              <Select
                value={empleadoSeleccionado?.id}
                onValueChange={(value) => {
                  const empleado = empleados.find(e => e.id === value);
                  setEmpleadoSeleccionado(empleado);
                }}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Selecciona un empleado" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {empleados.map(empleado => {
                    const getRoleName = (role) => {
                      const roles = {
                        'chatter': 'Chatter',
                        'asistente': 'Asistente',
                        'empleado': 'Empleado',
                        'administrativo': 'Administrativo',
                        'uploader': 'Uploader'
                      };
                      return roles[role] || role.charAt(0).toUpperCase() + role.slice(1);
                    };
                    
                    return (
                      <SelectItem key={empleado.id} value={empleado.id} className="text-white hover:bg-zinc-800">
                        {empleado.name} - {getRoleName(empleado.role)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen de estadísticas */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Días Trabajados</p>
                  <p className="text-3xl font-bold text-white">{resumen.dias_trabajados}</p>
                </div>
                <Users className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Horas</p>
                  <p className="text-3xl font-bold text-white">{resumen.total_horas} hrs</p>
                </div>
                <Clock className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Promedio Horas/Día</p>
                  <p className="text-3xl font-bold text-white">{resumen.promedio_horas_dia} hrs</p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendario */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              {meses[mesActual - 1]} {anoActual}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => cambiarMes('prev')}
                variant="outline"
                size="sm"
                className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
              >
                ← Anterior
              </Button>
              <Button
                onClick={() => cambiarMes('next')}
                variant="outline"
                size="sm"
                className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
              >
                Siguiente →
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Cargando calendario...</div>
          ) : (
            <div>
              {/* Encabezado de días de la semana */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(dia => (
                  <div key={dia} className="text-center text-gray-400 text-sm font-semibold py-2">
                    {dia}
                  </div>
                ))}
              </div>

              {/* Días del mes */}
              <div className="grid grid-cols-7 gap-2">
                {getDiasDelMes().map((dia, index) => {
                  const trabajado = esDiaTrabajado(dia);
                  const horasTrabajadas = getHorasTrabajadas(dia);
                  
                  return (
                    <div
                      key={index}
                      className={`
                        aspect-square flex flex-col items-center justify-center rounded-lg
                        transition-all cursor-pointer
                        ${
                          !dia 
                            ? 'bg-transparent' 
                            : trabajado
                            ? 'bg-green-900/40 border-2 border-green-500/50 hover:bg-green-900/60'
                            : 'bg-zinc-800 border border-zinc-700 hover:bg-zinc-700'
                        }
                      `}
                      title={trabajado && horasTrabajadas ? `${horasTrabajadas} horas trabajadas` : ''}
                    >
                      {dia && (
                        <>
                          <span className={`text-lg font-semibold ${
                            trabajado ? 'text-green-300' : 'text-gray-400'
                          }`}>
                            {dia}
                          </span>
                          {trabajado && horasTrabajadas && (
                            <span className="text-xs text-green-400 mt-1">
                              {horasTrabajadas}h
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Leyenda */}
              <div className="mt-6 flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-900/40 border-2 border-green-500/50"></div>
                  <span className="text-gray-400">Día Trabajado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-zinc-800 border border-zinc-700"></div>
                  <span className="text-gray-400">Día No Trabajado</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarioAsistencia;